<template>
  <div class="user-management">
    <!-- 搜索栏 -->
    <div class="filter-panel">
      <div class="search-container">
        <el-input placeholder="搜索用户昵称或邮箱" v-model="searchKeyword" class="search-input" size="small" clearable @keyup.enter.native="handleSearch" @clear="handleSearch">
          <template v-slot:prefix>
            <i class="el-input__icon el-icon-search"></i>
          </template>
        </el-input>
        <el-button type="primary" @click="handleSearch" icon="el-icon-search" size="small">搜索</el-button>
      </div>
    </div>

    <!-- 统计信息 -->
    <div class="content" v-loading="loading">
      <div class="filter-info" v-if="userList.length">
        共有 <span class="count-number">{{ pagination.total }}</span> 位用户
      </div>

      <el-table
        :data="userList"
        stripe
        style="width: 100%"
        border
        highlight-current-row>
        <el-table-column prop="_id" label="ID" width="80" align="center" show-overflow-tooltip>
          <template v-slot="scope">
            <span class="user-id">{{ formatId(scope.row._id) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="avatar" label="头像" width="90" align="center">
          <template v-slot="scope">
            <div class="avatar-wrapper">
              <el-image
                class="avatar-img"
                :src="getAvatarUrl(scope.row)"
                fit="cover"
              >
                <div slot="placeholder" class="avatar-placeholder"></div>
                <div slot="error" class="avatar-error">
                  <i class="el-icon-user" style="color: #c0c4cc; font-size: 20px;"></i>
                </div>
              </el-image>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="nickname" label="昵称" min-width="140" show-overflow-tooltip></el-table-column>
        <el-table-column prop="email" label="邮箱" min-width="180" show-overflow-tooltip></el-table-column>
        <el-table-column prop="points" label="积分" width="100" align="center">
          <template v-slot="scope">
            <span class="points-badge">{{ scope.row.points || 0 }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="isDoctorCertified" label="医生认证" width="110" align="center">
          <template v-slot="scope">
            <el-tag
              :type="getCertificationTagType(scope.row.isDoctorCertified)"
              size="small"
              effect="light"
            >
              {{ getCertificationText(scope.row.isDoctorCertified) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="注册时间" width="170" align="center"></el-table-column>
        <el-table-column label="操作" width="120" fixed="right" align="center">
          <template v-slot="scope">
            <el-button type="text" size="small" @click="editUser(scope.row._id)" class="edit-btn">
              <i class="el-icon-edit"></i> 编辑
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 空状态 -->
      <el-empty
        v-if="!loading && userList.length === 0"
        description="未找到用户数据"
        :image-size="150"
      >
        <template v-if="searchKeyword">
          <p class="empty-description">没有匹配到任何用户</p>
          <el-button type="primary" @click="clearSearch">清除搜索</el-button>
        </template>
      </el-empty>

      <div class="pagination-container">
        <el-pagination
          @current-change="handlePageChange"
          :current-page="pagination.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          @size-change="handleSizeChange"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total">
        </el-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import { getUserList, deleteUser } from "@/api/user";

export default {
  data() {
    return {
      loading: true,
      userList: [],
      searchKeyword: '',
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0,
      },
    };
  },
  methods: {
    getAvatarUrl(row) {
      const src = row && row.avatar ? String(row.avatar).trim() : '';
      if (!src) return '';
      try {
        const url = new URL(src, window.location.origin);
        url.searchParams.set('_t', String(Math.floor(Date.now() / 60000)));
        return url.toString();
      } catch (e) {
        return src;
      }
    },
    formatId(id) {
      if (!id) return '';
      return id.slice(-6);
    },
    getCertificationTagType(status) {
      if (status === 2) return 'success';
      if (status === 1) return 'warning';
      return 'info';
    },

    getCertificationText(status) {
      if (status === 2) return '已认证';
      if (status === 1) return '审核中';
      return '未认证';
    },
    handleSearch() {
      this.pagination.page = 1;
      this.getUserList();
    },
    clearSearch() {
      this.searchKeyword = '';
      this.handleSearch();
    },
    async getUserList() {
      this.loading = true;
      try {
        const param = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize,
          keywords: this.searchKeyword
        }
        const { data } = await getUserList(param);
        this.userList = data.users || [];
        this.pagination.total = data.total || 0;
      } catch (error) {
        this.$message.error('获取用户列表失败');
        this.userList = [];
      } finally {
        this.loading = false;
      }
    },
    handlePageChange(page) {
      this.pagination.page = page;
      this.getUserList();
    },
    handleSizeChange(size) {
      this.pagination.pageSize = size;
      this.pagination.page = 1;
      this.getUserList();
    },
    editUser(userId) {
      this.$router.push({ name: "user-edit", query: { userId } });
    },
  },
  mounted() {
    this.getUserList();
  },
};
</script>

<style scoped>
.user-management {
  background-color: #f5f7fa;
  min-height: 100%;
  padding: 20px;
  overflow-y: auto;
  height: calc(100vh - 84px);
}

.filter-panel {
  background-color: #fff;
  border-radius: 4px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.search-container {
  display: flex;
  gap: 10px;
  align-items: center;
}

.search-input {
  max-width: 320px;
}

.content {
  background-color: #fff;
  border-radius: 4px;
  padding: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.filter-info {
  margin-bottom: 15px;
  color: #606266;
  font-size: 14px;
}

.count-number {
  color: #409EFF;
  font-weight: bold;
}

/* 表格固定行高 */
::v-deep .el-table__body tr {
  height: 56px;
}
::v-deep .el-table__body td {
  padding: 8px 0;
}

/* 头像样式 */
.avatar-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #f2f3f5;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder,
.avatar-error {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f2f3f5;
}

/* ID样式 */
.user-id {
  font-family: monospace;
  color: #909399;
  font-size: 12px;
}

/* 积分徽章 */
.points-badge {
  display: inline-block;
  padding: 2px 10px;
  background-color: #ecf5ff;
  color: #409EFF;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
}

/* 认证状态 */
::v-deep .el-tag--success.is-light {
  background-color: #f0f9eb;
  border-color: #e1f3d8;
  color: #67c23a;
}

::v-deep .el-tag--warning.is-light {
  background-color: #fdf6ec;
  border-color: #faecd8;
  color: #e6a23c;
}

::v-deep .el-tag--info.is-light {
  background-color: #f4f4f5;
  border-color: #e9e9eb;
  color: #909399;
}

/* 操作按钮 */
.edit-btn {
  color: #409EFF;
}

.edit-btn:hover {
  color: #66b1ff;
}

/* 空状态 */
.empty-description {
  color: #909399;
  font-size: 14px;
  margin-top: 10px;
}

/* 分页 */
.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

/* 小屏幕适配 */
@media (max-width: 768px) {
  .user-management {
    padding: 10px;
  }

  .filter-panel {
    padding: 15px;
  }

  .search-container {
    flex-direction: column;
    align-items: stretch;
  }

  .search-input {
    max-width: 100%;
  }
}
</style>
